import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import imageIcon from "../../assets/image.png";
import { toast } from "react-toastify";
import { scrollToTop } from "../../components/ScrollToTop";
import { useAuth } from "../../contexts/AuthContext";
import { businessApi } from "../../services/api";
import { isNoBusinessError } from "../../services/onboarding";

const businessIdFromProfile = (profile) => {
  if (!profile) return null;
  let raw = profile.PK ?? profile.business_id;
  if (raw == null || raw === "") return null;
  let id = String(raw);
  if (id.startsWith("BUSINESS#")) id = id.slice("BUSINESS#".length);
  return id || null;
};

const Profile = () => {
  const navigate = useNavigate();
  const { tokens, logout, isSuperAdmin } = useAuth();
  const [businessName, setBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [website, setWebsite] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoURL, setLogoURL] = useState("");
  const [stripeVendorId, setStripeVendorId] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({
    phone: "",
    website: "",
  });
  const fileInputRef = useRef(null);

  // Validation functions
  const validatePhone = (phone) => {
    if (!phone) return "Phone number is required";
    // Allow phone numbers with +, digits, spaces, dashes, parentheses
    // Examples: +1 234 567 8900, (123) 456-7890, 123-456-7890, +1234567890
    const phoneRegex =
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(phone.trim())) {
      return "Please enter a valid phone number";
    }
    return "";
  };

  const validateWebsite = (website) => {
    if (!website) return "Website is required";
    // Require http:// or https:// protocol
    // Examples: https://example.com, http://example.com
    const websiteRegex =
      /^https?:\/\/([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
    if (!websiteRegex.test(website.trim())) {
      return "Please enter a valid website URL";
    }
    return "";
  };

  const [businessId, setBusinessId] = useState(null);

  // Fetch business profile via /me (same business PK as Stripe and other admin flows)
  useEffect(() => {
    const fetchProfile = async () => {
      if (!tokens.access_token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await businessApi.getMyProfile(tokens.access_token);
        const resolvedId = businessIdFromProfile(response);
        setBusinessId(resolvedId);
        if (!resolvedId) {
          toast.error(
            "Unable to determine your business ID from your profile. Please contact support.",
            { position: "top-right", autoClose: 4000 },
          );
        }

        // Populate form fields with API data
        if (response) {
          setBusinessName(response.name || "");
          setOwnerName(response.owner_name || "");
          setPhoneNumber(response.phone || "");
          setWebsite(response.website || "");
          setCompanyAddress(response.address || "");
          setBusinessDescription(response.description || "");
          setStripeVendorId(
            response.stripeVendorId || response.stripe_vendor_id || "",
          );
          setLogoURL(response.logoURL || "");
          if (response.logoURL) {
            setLogoPreview(response.logoURL);
          }
        }
      } catch (err) {
        console.error("🚨 [Profile] Error fetching profile:", err);
        const errorMessage =
          err?.error || err?.message || "Failed to load profile data.";

        // Check if error is related to authentication/authorization
        const errorMessageLower = errorMessage.toLowerCase();
        const isUnauthorized =
          errorMessageLower.includes("unauthorized") ||
          errorMessageLower.includes("invalid token") ||
          errorMessageLower.includes("expired") ||
          errorMessageLower.includes("token format") ||
          errorMessageLower.includes("authentication");

        if (isUnauthorized) {
          toast.error("Your session has expired. Please log in again.", {
            position: "top-right",
            autoClose: 4000,
          });
          // Logout and redirect to login
          logout();
          setTimeout(() => {
            navigate("/admin-login");
          }, 1500);
        } else if (isNoBusinessError(err) && !isSuperAdmin) {
          // A partner who never finished onboarding. Without this the form renders blank
          // and editable, they fill it in, and Save answers "Missing business ID or
          // authentication token" — naming the one thing that isn't broken.
          // super_admins legitimately have no business of their own and must not be
          // pushed into the partner onboarding flow.
          navigate("/create-profile");
        } else {
          toast.error(errorMessage, {
            position: "top-right",
            autoClose: 4000,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [tokens.access_token, logout, navigate]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Result = reader.result;
        setLogoPreview(base64Result);
        // Clear logoURL when new logo is uploaded (will use base64)
        setLogoURL("");
      };
      reader.readAsDataURL(file);
    }
  };

  // Wrapped rather than scrolled at each exit: this hands back its answer in seven places — a
  // rejected field, a missing business, an expired session, the success toast — and every one of
  // them is at the top of a form whose Save button is at the bottom. AC-SCROLL-F2.
  const handleProfileSave = async () => {
    try {
      await saveProfile();
    } finally {
      scrollToTop();
    }
  };

  const saveProfile = async () => {
    setAttemptedSubmit(true);

    // Validate phone and website
    const phoneError = validatePhone(phoneNumber);
    const websiteError = validateWebsite(website);

    setValidationErrors({
      phone: phoneError,
      website: websiteError,
    });

    let message = "";
    if (!businessName) message = "Business name is required";
    else if (!ownerName) message = "Owner name is required";
    else if (!phoneNumber) message = "Phone number is required";
    else if (phoneError) message = phoneError;
    else if (!website) message = "Website is required";
    else if (websiteError) message = websiteError;
    else if (!companyAddress) message = "Company address is required";
    else if (!businessDescription) message = "Business description is required";

    if (message) {
      toast.error(message, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // These were one condition, so a partner with no business was told their auth was
    // broken. They are different problems with different exits (#28). super_admins have
    // no business of their own, so they are not sent into partner onboarding.
    if (!businessId && !isSuperAdmin) {
      toast.info("Finish setting up your business first.", {
        position: "top-right",
        autoClose: 3000,
      });
      navigate("/create-profile");
      return;
    }

    if (!tokens.access_token) {
      toast.error("Missing authentication token. Please log in again.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      // Build request body - only include logoURL if a new logo was uploaded
      const requestBody = {
        business_id: businessId,
        name: businessName,
        owner_name: ownerName,
        phone: phoneNumber,
        website: website,
        address: companyAddress,
        description: businessDescription,
        // Inert, and kept only so the payload mirrors the record. The server treats
        // stripeVendorId as client-immutable and drops it from every public update
        // (business_profile.py CLIENT_IMMUTABLE_FIELDS), so this can neither set nor clear
        // the payout account — that filter is the write half of the #712 fix, which closed
        // an owner repointing their business at another brewery's Connect account. The
        // comment that stood here previously said an empty string would wipe the
        // connection; that stopped being true with #712.
        stripeVendorId: stripeVendorId || "",
      };

      // Only include logoURL if a new logo was uploaded (base64 data URL)
      if (logoPreview && logoPreview.startsWith("data:")) {
        requestBody.logoURL = logoPreview;
      }

      const response = await businessApi.updateProfile(
        requestBody,
        tokens.access_token,
      );

      // If we reach here, the API call was successful
      if (response && (response.PK || response.name)) {
        // Update logoURL with the response (in case it was uploaded to S3)
        if (response.logoURL) {
          setLogoURL(response.logoURL);
          setLogoPreview(response.logoURL);
        }
        toast.success("Profile updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("Failed to update profile. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("🚨 [Profile] Error updating profile:", err);

      // Extract error message from different possible error structures
      const errorMessage =
        err?.error ||
        err?.message ||
        (typeof err === "string" ? err : null) ||
        "Failed to update profile. Please try again.";

      // Check if error is related to authentication/authorization
      const errorMessageLower = errorMessage.toLowerCase();
      const isUnauthorized =
        errorMessageLower.includes("unauthorized") ||
        errorMessageLower.includes("invalid token") ||
        errorMessageLower.includes("expired") ||
        errorMessageLower.includes("token format") ||
        errorMessageLower.includes("authentication");

      if (isUnauthorized) {
        toast.error("Your session has expired. Please log in again.", {
          position: "top-right",
          autoClose: 4000,
        });
        // Logout and redirect to login
        logout();
        setTimeout(() => {
          navigate("/admin-login");
        }, 1500);
      } else {
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 4000,
        });
      }
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          fontSize: "18px",
          color: "#303030",
        }}
      >
        Loading profile...
      </div>
    );
  }

  return (
    <>
      <h2
        style={{
          textAlign: "left",
          marginBottom: "30px",
          fontSize: "clamp(24px, 6vw, 40px)",
          fontWeight: "bold",
          color: "#303030",
        }}
      >
        Profile
      </h2>

      {/* Row of 3 textfields */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", marginBottom: "20px" }}>
        <div style={{ flex: "1 1 220px", minWidth: 0, display: "flex", flexDirection: "column" }}>
          <label
            style={{ fontSize: "14px", color: "black", marginBottom: "6px" }}
          >
            Business Name <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            style={{
              padding: "12px",
              borderRadius: "8px",
              border:
                businessName === "" && attemptedSubmit
                  ? "1px solid red"
                  : "1px solid #ccc",
            }}
          />
          {businessName === "" && attemptedSubmit && (
            <span
              style={{
                color: "red",
                fontStyle: "italic",
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              Business Name is required
            </span>
          )}
        </div>
        <div style={{ flex: "1 1 220px", minWidth: 0, display: "flex", flexDirection: "column" }}>
          <label
            style={{ fontSize: "14px", color: "black", marginBottom: "6px" }}
          >
            Owner's Name <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            style={{
              padding: "12px",
              borderRadius: "8px",
              border:
                ownerName === "" && attemptedSubmit
                  ? "1px solid red"
                  : "1px solid #ccc",
            }}
          />
          {ownerName === "" && attemptedSubmit && (
            <span
              style={{
                color: "red",
                fontStyle: "italic",
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              Owner Name is required
            </span>
          )}
        </div>
        <div style={{ flex: "1 1 220px", minWidth: 0, display: "flex", flexDirection: "column" }}>
          <label
            style={{ fontSize: "14px", color: "black", marginBottom: "6px" }}
          >
            Phone Number (+1) <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              // Clear error when user starts typing
              if (validationErrors.phone) {
                setValidationErrors({
                  ...validationErrors,
                  phone: "",
                });
              }
            }}
            onBlur={() => {
              const error = validatePhone(phoneNumber);
              setValidationErrors({
                ...validationErrors,
                phone: error,
              });
            }}
            style={{
              padding: "12px",
              borderRadius: "8px",
              border:
                (phoneNumber === "" && attemptedSubmit) ||
                validationErrors.phone
                  ? "1px solid red"
                  : "1px solid #ccc",
            }}
          />
          {(phoneNumber === "" && attemptedSubmit) || validationErrors.phone ? (
            <span
              style={{
                color: "red",
                fontStyle: "italic",
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {phoneNumber === "" && attemptedSubmit
                ? "Phone Number is required"
                : validationErrors.phone}
            </span>
          ) : null}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", marginBottom: "20px" }}>
        <div style={{ flex: "1 1 220px", minWidth: 0, display: "flex", flexDirection: "column" }}>
          <label
            style={{
              fontSize: "14px",
              color: "black",
              marginBottom: "6px",
              display: "block",
            }}
          >
            Website <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="url"
            value={website}
            onChange={(e) => {
              setWebsite(e.target.value);
              // Clear error when user starts typing
              if (validationErrors.website) {
                setValidationErrors({
                  ...validationErrors,
                  website: "",
                });
              }
            }}
            onBlur={() => {
              const error = validateWebsite(website);
              setValidationErrors({
                ...validationErrors,
                website: error,
              });
            }}
            placeholder="https://example.com"
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "8px",
              border:
                (website === "" && attemptedSubmit) || validationErrors.website
                  ? "1px solid red"
                  : "1px solid #ccc",
              boxSizing: "border-box",
            }}
          />
          {(website === "" && attemptedSubmit) || validationErrors.website ? (
            <span
              style={{
                color: "red",
                fontStyle: "italic",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {website === "" && attemptedSubmit
                ? "Website is required"
                : validationErrors.website}
            </span>
          ) : null}
        </div>
        <div style={{ flex: "1 1 220px", minWidth: 0, display: "flex", flexDirection: "column" }}>
          <label
            style={{
              fontSize: "14px",
              color: "black",
              marginBottom: "6px",
              display: "block",
            }}
          >
            Company Address <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            value={companyAddress}
            onChange={(e) => setCompanyAddress(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "8px",
              border:
                companyAddress === "" && attemptedSubmit
                  ? "1px solid red"
                  : "1px solid #ccc",
              boxSizing: "border-box",
            }}
          />
          {companyAddress === "" && attemptedSubmit && (
            <span
              style={{
                color: "red",
                fontStyle: "italic",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Company Address is required
            </span>
          )}
        </div>
      </div>

      {/* Business Description */}
      <div style={{ marginBottom: "30px" }}>
        <label
          style={{
            fontSize: "14px",
            color: "black",
            marginBottom: "6px",
            display: "block",
          }}
        >
          Business Description <span style={{ color: "red" }}>*</span>
        </label>
        <textarea
          value={businessDescription}
          onChange={(e) => setBusinessDescription(e.target.value)}
          rows={4}
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: "8px",
            border:
              businessDescription === "" && attemptedSubmit
                ? "1px solid red"
                : "1px solid #ccc",
            boxSizing: "border-box",
            resize: "vertical",
            fontFamily: "inherit",
          }}
        />
        {businessDescription === "" && attemptedSubmit && (
          <span
            style={{
              color: "red",
              fontStyle: "italic",
              fontSize: "12px",
              marginTop: "4px",
              display: "block",
            }}
          >
            Business Description is required
          </span>
        )}
      </div>

      {/* Logo Upload */}
      <div
        style={{
          display: "flex",
          // Wrap rather than squeeze: unwrapped, the preview box was flex-shrunk to a
          // non-square sliver and the button's label broke across three lines inside a
          // pill radius. AC-MOBILE-F1.
          flexWrap: "wrap",
          alignItems: "center",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            width: "200px",
            height: "200px",
            flexShrink: 0,
            backgroundColor: "rgba(222, 129, 14, 0.1)",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #ccc",
            overflow: "hidden",
          }}
        >
          {logoPreview ? (
            <img
              src={logoPreview}
              alt="logo preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <img
              src={imageIcon}
              alt="logo placeholder"
              style={{ width: "40px", height: "40px", opacity: 0.6 }}
            />
          )}
        </div>
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleLogoUpload}
            accept="image/*"
            style={{ display: "none" }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding: "12px 24px",
              borderRadius: "9999px",
              whiteSpace: "nowrap",
              border: "1px solid #DE810E",
              cursor: "pointer",
              background: "white",
              fontWeight: "bold",
              color: "#DE810E",
              fontSize: "16px",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            Upload Company Logo
          </button>
        </div>
      </div>

      {/* Edit/Save Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <button
          onClick={handleProfileSave}
          style={{
            marginTop: "30px",
            width: "167px",
            height: "59px",
            borderRadius: "9999px",
            border: "none",
            cursor: "pointer",
            background: "#DE810E",
            color: "white",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          Save Profile
        </button>
      </div>
    </>
  );
};

export default Profile;
